import { useEffect, useState } from 'react'
import { Term } from './Term'

const DISMISSED_KEY = 'dota-oracle:explainer-dismissed'

/**
 * What this site is, for someone who has just arrived.
 *
 * The pages assume a working knowledge of decimal odds, implied probability and the bookmaker's
 * margin. Most visitors will have none of that, and the numbers are actively misleading without it —
 * "1.79 or better" reads like a prediction rather than a threshold, and a 62% call next to a 1.55
 * price looks like agreement when it is the opposite.
 *
 * Dismissable, and the dismissal persists. A permanent banner is something regulars learn to scroll
 * past, which is the same as not having written it. `localStorage` is wrapped because it throws
 * outright in a browser with site data blocked, and an explanatory panel is not worth a blank page.
 */
export function Explainer() {
  const [dismissed, setDismissed] = useState(true)

  // Read after mount rather than in a useState initialiser: this component is also rendered by the
  // jsdom smoke test, where reaching for storage during the first render would throw before
  // anything reaches the DOM.
  useEffect(() => {
    setDismissed(read(DISMISSED_KEY) === '1')
  }, [])

  if (dismissed) {
    return (
      <button
        type="button"
        onClick={() => {
          write(DISMISSED_KEY, '')
          setDismissed(false)
        }}
        className="text-xs text-ink-faint underline decoration-dotted underline-offset-4 hover:text-ink-soft"
      >
        How do I read this?
      </button>
    )
  }

  return (
    <section className="rounded-lg border border-line bg-surface p-4">
      <div className="flex items-start justify-between gap-4">
        <h2 className="text-sm font-medium">How to read this site</h2>
        <button
          type="button"
          onClick={() => {
            write(DISMISSED_KEY, '1')
            setDismissed(true)
          }}
          className="shrink-0 text-xs text-ink-faint hover:text-ink"
        >
          Dismiss
        </button>
      </div>

      {/*
        Two paragraphs, not a three-column slab.

        It used to teach three lessons in 250px above the fold. Two of them — what "1.79 or better"
        means, and why a bookmaker's margin is not an opinion — are now the definitions behind
        `break-even`, `devig` and `margin`, which sit as tap targets on the cards immediately above
        this panel. Teaching a word here and again where it is used is the same fault as printing a
        caption under every card, one level up.
        
        The third lesson also had to go regardless: it said the estimate "has never seen the draft",
        written before the post-draft head shipped.

        What is left is the part nothing else says — what the number IS — and a pointer to the
        mechanism that teaches the rest in place.
      */}
      <p className="mt-2 text-sm text-ink-soft">
        Every percentage is our estimate that a team wins, built only from games played{' '}
        <em>before</em> the match. Beside it we print the price that estimate would have to beat to
        be worth backing — not a prediction, a threshold. A bet is advised when the price on offer
        clears it, so the two numbers are one sum and cannot disagree.
      </p>

      <p className="mt-2 text-sm text-ink-soft">
        Any word with a dotted underline explains itself: tap{' '}
        <Term id="break-even">Worth backing at</Term>, <Term id="devig" />, <Term id="edge" /> or{' '}
        <Term id="odds" /> wherever you meet them.
      </p>
    </section>
  )
}

/** localStorage throws, not returns null, when site data is blocked. */
function read(key: string): string | null {
  try {
    return window.localStorage.getItem(key)
  } catch {
    return null
  }
}

function write(key: string, value: string) {
  try {
    window.localStorage.setItem(key, value)
  } catch {
    // A preference we cannot persist is not worth breaking the page over.
  }
}
